import React, { useState, useEffect, useRef } from "react";
import {
  Building2,
  Search,
  Plus,
  ChevronRight,
  Users,
  MapPin,
  Edit,
  UserPlus,
  MoreVertical,
  RefreshCw,
  Filter,
  X,
  Lock,
  CheckCircle,
  XCircle,
  Building,
  ChevronDown,
  Network,
} from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useSuspension } from "../../contexts/SuspensionContext";
import {
  fetchChildOrganizationsAPI,
  fetchChildOrgDetailsAPI,
} from "../../actioncreators/actioncreators";
import AddChildOrganization from "./AddChildOrganization";
import AssignChildOrgAdmin from "./AssignChildOrgAdmin";
import ChildOrgDetails from "./ChildOrgDetails";

const ChildOrganizations = () => {
  const [childOrgs, setChildOrgs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const isMountedRef = useRef(false);
  const dropdownRef = useRef(null);

  const loginResponse = useSelector((state) => state.loginResponse);
  const { isSuspended } = useSuspension();
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === "SUPER_ADMIN";
  const isOrgAdmin = loginResponse?.role_name?.toUpperCase() === "ADMIN";

  // Fetch child organizations
  const fetchChildOrgs = async () => {
    setIsLoading(true);
    try {
      const response = await fetchChildOrganizationsAPI({
        search: searchTerm,
        status: statusFilter,
      });

      if (response?.businessStatusCode === 2) {
        setChildOrgs(response.data || []);
      } else {
        toast.error(response?.message || "Failed to fetch organizations");
      }
    } catch (error) {
      console.error("Error fetching child organizations:", error);
      toast.error("Failed to fetch organizations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      fetchChildOrgs();
    }
  }, []);

  useEffect(() => {
    if (isMountedRef.current) {
      const debounce = setTimeout(() => {
        fetchChildOrgs();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, statusFilter]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter organizations
  const filteredOrgs = childOrgs.filter((org) => {
    const matchesSearch =
      org.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.admin_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || org.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group by hierarchy level
  const groupedOrgs = filteredOrgs.reduce((acc, org) => {
    const level = org.hierarchy_level || 0;
    if (!acc[level]) acc[level] = [];
    acc[level].push(org);
    return acc;
  }, {});

  const handleRowClick = (org) => {
    setSelectedOrg(org);
    setShowDetailsPanel(true);
  };

  const handleAssignAdmin = (org) => {
    setSelectedOrg(org);
    setShowAssignAdminModal(true);
    setActiveDropdown(null);
  };

  const handleOrgCreated = () => {
    setShowAddModal(false);
    fetchChildOrgs();
    toast.success("Organization created successfully");
  };

  const handleAdminAssigned = () => {
    setShowAssignAdminModal(false);
    fetchChildOrgs();
    toast.success("Admin assigned successfully");
  };

  const getStatusBadge = (status) => {
    const isActive = status === "ACTIVE";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "600",
          backgroundColor: isActive ? "#d1fae5" : "#fee2e2",
          color: isActive ? "#059669" : "#dc2626",
        }}
      >
        {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
        {status}
      </span>
    );
  };

  const getLevelBadge = (level) => {
    const colors = [
      { bg: "#fef3c7", text: "#d97706" }, // Level 0
      { bg: "#dbeafe", text: "#2563eb" }, // Level 1
      { bg: "#e0e7ff", text: "#4f46e5" }, // Level 2
      { bg: "#fce7f3", text: "#be185d" }, // Level 3
      { bg: "#f3e8ff", text: "#7c3aed" }, // Level 4+
    ];
    const colorIndex = Math.min(level, colors.length - 1);
    return (
      <span
        style={{
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: "600",
          backgroundColor: colors[colorIndex].bg,
          color: colors[colorIndex].text,
        }}
      >
        L{level}
      </span>
    );
  };

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1f2937",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Network size={28} color="#ff7a00" />
            Child Organizations
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              margin: "4px 0 0 0",
            }}
          >
            Manage your organization hierarchy and assign administrators
          </p>
        </div>

        <button
          onClick={() => !isSuspended && setShowAddModal(true)}
          disabled={isSuspended}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 20px",
            backgroundColor: isSuspended ? "#9ca3af" : "#ff7a00",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: isSuspended ? "not-allowed" : "pointer",
            boxShadow: "0 2px 8px rgba(255, 122, 0, 0.3)",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            if (!isSuspended) {
              e.target.style.backgroundColor = "#e56b00";
              e.target.style.transform = "translateY(-1px)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isSuspended) {
              e.target.style.backgroundColor = "#ff7a00";
              e.target.style.transform = "translateY(0)";
            }
          }}
          title={isSuspended ? "Organization is suspended" : ""}
        >
          {isSuspended ? <Lock size={16} /> : <Plus size={16} />}
          Add Organization
        </button>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                Total Organizations
              </p>
              <h3
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#1f2937",
                  margin: "4px 0 0 0",
                }}
              >
                {childOrgs.length}
              </h3>
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#fff7ed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={24} color="#ff7a00" />
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                Active Organizations
              </p>
              <h3
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#10b981",
                  margin: "4px 0 0 0",
                }}
              >
                {childOrgs.filter((o) => o.status === "ACTIVE").length}
              </h3>
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#d1fae5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle size={24} color="#10b981" />
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                Total Users
              </p>
              <h3
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#2563eb",
                  margin: "4px 0 0 0",
                }}
              >
                {childOrgs.reduce((sum, o) => sum + (o.user_count || 0), 0)}
              </h3>
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Users size={24} color="#2563eb" />
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                Total Locations
              </p>
              <h3
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#7c3aed",
                  margin: "4px 0 0 0",
                }}
              >
                {childOrgs.reduce((sum, o) => sum + (o.location_count || 0), 0)}
              </h3>
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#f3e8ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MapPin size={24} color="#7c3aed" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "16px 20px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e5e7eb",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {/* Search */}
          <div
            style={{
              flex: 1,
              minWidth: "250px",
              position: "relative",
            }}
          >
            <Search
              size={18}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              type="text"
              placeholder="Search organizations or admins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px 10px 40px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#ff7a00")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "10px 32px 10px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#fff",
              cursor: "pointer",
              outline: "none",
              appearance: "none",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 8px center",
              backgroundSize: "16px",
            }}
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          {/* Refresh */}
          <button
            onClick={fetchChildOrgs}
            disabled={isLoading}
            style={{
              padding: "10px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RefreshCw
              size={18}
              color="#6b7280"
              className={isLoading ? "spin" : ""}
            />
          </button>
        </div>
      </div>

      {/* Organizations Table */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 80px",
            padding: "14px 20px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            fontSize: "11px",
            fontWeight: "600",
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <div>Organization</div>
          <div>Admin</div>
          <div style={{ textAlign: "center" }}>Users</div>
          <div style={{ textAlign: "center" }}>Locations</div>
          <div style={{ textAlign: "center" }}>Status</div>
          <div style={{ textAlign: "center" }}>Actions</div>
        </div>

        {/* Table Body */}
        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
          {isLoading ? (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: "#6b7280",
              }}
            >
              <RefreshCw
                size={32}
                color="#ff7a00"
                style={{ animation: "spin 1s linear infinite" }}
              />
              <p style={{ marginTop: "12px" }}>Loading organizations...</p>
            </div>
          ) : filteredOrgs.length === 0 ? (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: "#6b7280",
              }}
            >
              <Building2 size={48} color="#d1d5db" />
              <p style={{ marginTop: "12px", fontSize: "14px" }}>
                No child organizations found
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  marginTop: "16px",
                  padding: "10px 20px",
                  backgroundColor: "#ff7a00",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Create First Organization
              </button>
            </div>
          ) : (
            filteredOrgs.map((org, index) => (
              <div
                key={org.org_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 80px",
                  padding: "16px 20px",
                  borderBottom:
                    index < filteredOrgs.length - 1
                      ? "1px solid #f3f4f6"
                      : "none",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => handleRowClick(org)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#fff")
                }
              >
                {/* Organization */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      backgroundColor: "#fff7ed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Building2 size={20} color="#ff7a00" />
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {org.org_name}
                      </span>
                      {getLevelBadge(org.hierarchy_level)}
                    </div>
                    {org.parent_org_name && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#9ca3af",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <ChevronRight size={12} />
                        {org.parent_org_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Admin */}
                <div>
                  {org.admin_name ? (
                    <div>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "1000",
                          color: "#1f2937",
                          margin: 0,
                        }}
                      >
                        {org.admin_name}
                      </p>
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#9ca3af",
                          margin: 0,
                        }}
                      >
                        {org.admin_email}
                      </p>
                    </div>
                  ) : (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#f59e0b",
                        fontStyle: "italic",
                      }}
                    >
                      No admin assigned
                    </span>
                  )}
                </div>

                {/* Users */}
                <div style={{ textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "13px",
                      fontWeight: "1000",
                      color: "#1f2937",
                    }}
                  >
                    <Users size={14} color="#6b7280" />
                    {org.user_count || 0}
                  </span>
                </div>

                {/* Locations */}
                <div style={{ textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "13px",
                      fontWeight: "1000",
                      color: "#1f2937",
                    }}
                  >
                    <MapPin size={14} color="#6b7280" />
                    {org.location_count || 0}
                  </span>
                </div>

                {/* Status */}
                <div style={{ textAlign: "center" }}>
                  {getStatusBadge(org.status)}
                </div>

                {/* Actions */}
                <div
                  style={{ textAlign: "center", position: "relative" }}
                  ref={activeDropdown === org.org_id ? dropdownRef : null}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveDropdown(
                        activeDropdown === org.org_id ? null : org.org_id
                      );
                    }}
                    style={{
                      padding: "6px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <MoreVertical size={16} color="#6b7280" />
                  </button>

                  {activeDropdown === org.org_id && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: "4px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        border: "1px solid #e5e7eb",
                        zIndex: 10,
                        minWidth: "160px",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(org);
                          setActiveDropdown(null);
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          border: "none",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                          color: "#374151",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#f3f4f6")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                      >
                        <Building size={14} />
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssignAdmin(org);
                        }}
                        disabled={isSuspended}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          border: "none",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                          color: isSuspended ? "#9ca3af" : "#374151",
                          cursor: isSuspended ? "not-allowed" : "pointer",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSuspended)
                            e.target.style.backgroundColor = "#f3f4f6";
                        }}
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                      >
                        {isSuspended ? <Lock size={14} /> : <UserPlus size={14} />}
                        {org.admin_name ? "Change Admin" : "Assign Admin"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Child Org Modal */}
      {showAddModal && (
        <AddChildOrganization
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleOrgCreated}
        />
      )}

      {/* Assign Admin Modal */}
      {showAssignAdminModal && selectedOrg && (
        <AssignChildOrgAdmin
          isOpen={showAssignAdminModal}
          onClose={() => setShowAssignAdminModal(false)}
          organization={selectedOrg}
          onSuccess={handleAdminAssigned}
        />
      )}

      {/* Details Side Panel */}
      {showDetailsPanel && selectedOrg && (
        <ChildOrgDetails
          isOpen={showDetailsPanel}
          onClose={() => setShowDetailsPanel(false)}
          orgId={selectedOrg.org_id}
          onAssignAdmin={() => {
            setShowDetailsPanel(false);
            setShowAssignAdminModal(true);
          }}
        />
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default ChildOrganizations;
